import { autorun, observe } from 'mobx'
import { applySnapshot, flow, Instance, types } from 'mobx-state-tree'
import API from '../../api'
import { ILogInRequest, IRegisterRequest } from '../../api/sections/auth/types'
import logs from '../../utils/logs'
import StorageItem from '../../utils/storage-item'


const authStateStorage = new StorageItem<{
    token: string | null
}>(localStorage, 'authStore:authState')



export const AuthStore = types
  .model('AuthStore', {
    token: types.maybeNull(types.string),
  })
  .actions(self => ({
    reset() {
      applySnapshot(self, {})
    },
  }))
  .actions(self => ({
    login: flow(function*(data: ILogInRequest) {
      try {
        const { access_token } = yield API.auth.logIn(data)

        self.token = access_token

        return true
      } catch (e) {
        logs.error('Authorization error', e)
      }

      return false
    }),
    signUp: flow(function*(data: IRegisterRequest) {
      try {
        const { accessToken } = yield API.auth.register({
          ...data,
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localeId:
            window.navigator.language || (window.navigator as any).userLanguage,
        })
        self.token = accessToken.access_token
        return true
      } catch (e) {
        logs.error('Registration error', e)
      }
      return false
    }),
    logout: flow(function*() {
      yield API.auth.logOut()
      self.reset()
    }),
  }))
  .views(self => ({
    get isLoggedIn() {
      return !!self.token
    },
  }))
  .actions(self => {
    let cacheReactionDisposer: any = null

    return {
      initCache: () => {
        if (!cacheReactionDisposer) {
          cacheReactionDisposer = autorun(() => {
              authStateStorage.stringifyAndSet({
                token: self.token,
              })
          })
        }
      },
      restoreFromCache: () => {
          const cachedState = authStateStorage.getParsed()

          if (!cachedState) {
            return
          }

          const { token } = cachedState

          if (self.token) {
            return
          }

          self.token = token
        
      },
      clearCache: () => {
        authStateStorage.clear()
      },
    }
  })
  .actions(self => ({
    afterCreate() {
      self.restoreFromCache()
      self.initCache()

      observe(self, 'isLoggedIn', (props: any) => {
        if (!props.newValue) {
          self.clearCache()
        }
      })
    },
  }))

export interface IAuthStore extends Instance<typeof AuthStore> {}
